import React, { useEffect, useState, useMemo } from 'react';
import { Dropdown, Button, Space, Spin, Input, Divider } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { BranchesType, PropertyCode } from 'services/api/type/branch.type';
import 'styles/branches-dropdown.scss';
import { setBranch, getBranch } from 'services/storages/userStorage';
import { useGetAllBranches } from 'hooks/useGetAllBranches';
import { useTranslation } from 'react-i18next';

const displayBranchName = (name: string) =>
  name?.replace(/\s*\(ARCHIVED\)\s*/gi, '').trim() ?? name;

const isArchivedBranch = (name: string) => /\(ARCHIVED\)/i.test(name ?? '');

const normalizePropertyCode = (propertyCode: unknown): string => {
  if (Array.isArray(propertyCode))
    return propertyCode
      .map(v => normalizePropertyCode(v))
      .filter(Boolean)
      .join(', ');
  if (typeof propertyCode === 'string') return propertyCode;
  if (typeof propertyCode === 'number') return String(propertyCode);
  if (propertyCode && typeof propertyCode === 'object') {
    const maybe = propertyCode as PropertyCode;
    const candidate = maybe.property_code ?? maybe.code ?? maybe.value ?? maybe.name;
    if (typeof candidate === 'string') return candidate;
    if (typeof candidate === 'number') return String(candidate);
  }
  return '';
};

const normalizePropertyCodeForService = (propertyCode: unknown): string =>
  normalizePropertyCode(propertyCode)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const BranchesDropdown: React.FC = () => {
  const { t } = useTranslation();
  const { data: branches, isLoading: loading } = useGetAllBranches();
  const [selectedBranch, setSelectedBranch] = useState<BranchesType | null>(null);
  const [searchText, setSearchText] = useState('');

  const visibleBranches = useMemo(
    () => (branches ?? []).filter(b => b.is_active || !isArchivedBranch(b.name)),
    [branches]
  );

  useEffect(() => {
    if (!visibleBranches.length) return;
    const branch = visibleBranches.find(b => b.id === getBranch()) ?? visibleBranches[0];
    setSelectedBranch(branch);
    setBranch(branch.id);
  }, [visibleBranches]);

  const filteredBranches = useMemo(() => {
    const raw = searchText.trim().toLowerCase();
    const normalized = raw.replace(/[^a-z0-9]/g, '');
    if (!raw) return visibleBranches;

    return visibleBranches.filter(branch => {
      const name = displayBranchName(branch.name ?? '').toLowerCase();
      const propertyCodeRaw = normalizePropertyCode(branch.property_code).toLowerCase();
      const propertyCodeService = normalizePropertyCodeForService(branch.property_code);

      return (
        name.includes(raw) ||
        propertyCodeRaw.includes(raw) ||
        propertyCodeService.includes(normalized)
      );
    });
  }, [visibleBranches, searchText]);

  const handleMenuClick = (branch: BranchesType) => {
    setSelectedBranch(branch);
    setBranch(branch.id);
    window.location.reload();
  };

  const items: MenuProps['items'] = loading
    ? [{ label: <Spin size="small" />, key: 'loading' }]
    : !filteredBranches.length
      ? [
          {
            label: <div style={{ padding: '4px 0', opacity: 0.7 }}>No branches found</div>,
            key: 'empty',
            disabled: true
          }
        ]
      : filteredBranches.map((branch, index) => ({
          key: branch.id,
          label: (
            <div
              onClick={() => handleMenuClick(branch)}
              style={{ width: '100%', cursor: 'pointer', padding: '4px 0' }}
            >
              <div className="branchesDropdownItem">
                <div className="branchesDropdownItemTitle">
                  {index + 1} - {displayBranchName(branch.name)}
                </div>
                {normalizePropertyCode(branch.property_code) && (
                  <div className="branchesDropdownItemMeta">
                    {normalizePropertyCode(branch.property_code)}
                  </div>
                )}
              </div>
            </div>
          )
        }));

  const selectedBranchIndex = selectedBranch
    ? visibleBranches.findIndex(b => b.id === selectedBranch.id) + 1
    : 0;

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      className="customDropdown"
      dropdownRender={menu => (
        <div className="branchesDropdownOverlay" onClick={e => e.stopPropagation()}>
          <div className="branchesDropdownFilters">
            <Input
              size="small"
              allowClear
              autoFocus
              placeholder="Search by branch name or property code"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <Divider style={{ margin: '10px 0' }} />
          </div>
          {menu}
        </div>
      )}
    >
      <Button className="customButton">
        <Space>
          {selectedBranch
            ? `${selectedBranchIndex} - ${displayBranchName(selectedBranch.name)}`
            : t('common.selectBranch')}
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );
};

export default BranchesDropdown;
